import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "../shared/UserContext";
import useFetchClasses from "../hooks/useTeacherClasses";
import useFilteredStudents from "../hooks/useFilteredStudents";
import useChecklists from "../hooks/useChecklists";
import {
  createChecklist, renameChecklist, deleteChecklist, toggleCheck, resetChecklist,
  setChecklistColor, CHECKLIST_COLORS,
} from "../shared/checklistData";
import StudentCheckRow from "./StudentCheckRow";
import "./Checklist.css";
import { surnameKey } from "../shared/nameSort";

// Everyday, always-available scratch checklist (field-trip money, head counts,
// permission slips…). Separate from attendance; attendance only greys "Absent"
// students as context. Shown when no emergency is active.
export default function ChecklistMode() {
  const { uid, schoolId } = useUser();
  const { classList } = useFetchClasses();
  const activeClasses = classList.filter((c) => !c.isArchived);

  const classKey = uid ? `checklist-class-${uid}` : null;
  const [selectedClassId, setSelectedClassId] = useState("");
  const [activeChecklistId, setActiveChecklistId] = useState(null);
  // Checklist name+color modal — used for both New and Rename (editId = null → new).
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newColor, setNewColor] = useState(CHECKLIST_COLORS[0]);

  const { checklists } = useChecklists(selectedClassId);
  const { presentStudents, notPresentStudents } = useFilteredStudents(selectedClassId);

  // NOTE: no auto-create. Each class starts with no checklist; the teacher creates
  // what they need per class (homeroom vs others) via "+ New" / the empty-state
  // button. (The old auto-create re-spawned blank checklists after deletion and
  // duplicated on remount — so a class with none now simply shows the empty state.)

  // Pick a class on load (remember the last one used).
  useEffect(() => {
    if (!activeClasses.length) return;
    setSelectedClassId((prev) => {
      if (prev && activeClasses.some((c) => c.id === prev)) return prev;
      const saved = classKey ? localStorage.getItem(classKey) : null;
      if (saved && activeClasses.some((c) => c.id === saved)) return saved;
      return activeClasses[0].id;
    });
  }, [activeClasses, classKey]);

  // Keep an active checklist selected as the list loads / class changes.
  useEffect(() => {
    setActiveChecklistId((prev) => {
      if (prev && checklists.some((c) => c.id === prev)) return prev;
      return checklists[0]?.id ?? null;
    });
  }, [checklists]);

  const selectClass = (id) => {
    setSelectedClassId(id);
    setActiveChecklistId(null);
    if (classKey) localStorage.setItem(classKey, id);
  };

  const active = checklists.find((c) => c.id === activeChecklistId) || null;
  const checkedSet = useMemo(
    () => new Set(active?.checkedStudentIds || []),
    [active],
  );

  // Roster with present/absent context (from today's attendance). Only grey/label
  // students "Absent" when attendance has actually been taken — otherwise (no
  // attendance yet) everyone would show muted, so treat them all as present.
  const attendanceTaken = presentStudents.length > 0;
  const roster = useMemo(() => {
    const rows = [
      ...presentStudents.map((s) => ({ id: s.id, name: s.studentName, lastName: s.lastName, present: true })),
      ...notPresentStudents.map((s) => ({ id: s.id, name: s.studentName, lastName: s.lastName, present: !attendanceTaken })),
    ];
    return rows;
  }, [presentStudents, notPresentStudents, attendanceTaken]);

  const unchecked = roster
    .filter((s) => !checkedSet.has(s.id))
    .sort((a, b) => (a.present === b.present ? surnameKey(a).localeCompare(surnameKey(b)) : a.present ? -1 : 1));
  const checked = roster
    .filter((s) => checkedSet.has(s.id))
    .sort((a, b) => surnameKey(a).localeCompare(surnameKey(b)));

  const openCreate = () => {
    setEditId(null);
    setNewTitle("");
    setNewColor(CHECKLIST_COLORS[0]);
    setShowCreate(true);
  };
  const openRename = () => {
    if (!active) return;
    setEditId(active.id);
    setNewTitle(active.title || "");
    setNewColor(active.color || CHECKLIST_COLORS[0]);
    setShowCreate(true);
  };
  const handleSaveModal = async () => {
    if (!newTitle.trim()) return;
    if (editId) {
      await renameChecklist(editId, newTitle.trim());
      await setChecklistColor(editId, newColor);
    } else {
      if (!selectedClassId) return;
      const ref = await createChecklist({
        ownerUid: uid, schoolId, classId: selectedClassId, title: newTitle.trim(), color: newColor,
      });
      setActiveChecklistId(ref.id);
    }
    setShowCreate(false);
  };
  const handleReset = async () => {
    if (!active) return;
    if (window.confirm(`Clear all checks on "${active.title}"?`)) await resetChecklist(active.id);
  };
  const handleDelete = async () => {
    if (!active) return;
    if (window.confirm(`Delete the checklist "${active.title}"? This can't be undone.`)) {
      await deleteChecklist(active.id);
      setActiveChecklistId(null);
    }
  };
  const handleToggle = (studentId) => {
    if (!active) return;
    toggleCheck(active.id, studentId, !checkedSet.has(studentId));
  };

  const selectedClass = activeClasses.find((c) => c.id === selectedClassId);

  if (!activeClasses.length) {
    return (
      <div className="checklist-page">
        <h2>Check List</h2>
        <p className="checklist-empty">You don't have any classes yet. Add a class to start a checklist.</p>
      </div>
    );
  }

  return (
    <div className="checklist-page">
      {showCreate && (
        <div className="cl-modal-backdrop" onClick={() => setShowCreate(false)}>
          <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{editId ? "Edit checklist" : "New checklist"}</h3>
            <label className="cl-modal-label">Name</label>
            <input
              className="cl-modal-input"
              type="text"
              autoFocus
              placeholder="e.g. Permission slips, Chromebooks returned"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSaveModal()}
            />
            <label className="cl-modal-label">Color</label>
            <div className="cl-swatches">
              {CHECKLIST_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`cl-swatch ${newColor === c ? "is-selected" : ""}`}
                  style={{ background: c }}
                  onClick={() => setNewColor(c)}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>
            <div className="cl-modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              <button onClick={handleSaveModal} disabled={!newTitle.trim()}>{editId ? "Save" : "Create"}</button>
            </div>
          </div>
        </div>
      )}
      <div className="checklist-head">
        <h2>Check List</h2>
        {activeClasses.length > 1 ? (
          <select
            className="checklist-class-select"
            value={selectedClassId}
            onChange={(e) => selectClass(e.target.value)}
          >
            {activeClasses.map((c) => (
              <option key={c.id} value={c.id}>{c.className}</option>
            ))}
          </select>
        ) : (
          <div className="checklist-title">{selectedClass?.className}</div>
        )}

        <div className="checklist-tabs">
          {/* Phones: a dropdown to switch checklists — but only when there's more
              than one (hidden otherwise). Desktop uses the pill tabs. */}
          {checklists.length > 1 && (
            <select
              className="checklist-tab-select"
              value={activeChecklistId || ""}
              onChange={(e) => setActiveChecklistId(e.target.value)}
              aria-label="Select checklist"
            >
              {checklists.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          )}
          {checklists.map((c) => {
            const isActive = c.id === activeChecklistId;
            const color = c.color || "var(--color-primary)";
            return (
              <button
                key={c.id}
                className={`checklist-tab ${isActive ? "active" : ""}`}
                onClick={() => setActiveChecklistId(c.id)}
                style={isActive ? { color, borderBottomColor: color } : undefined}
              >
                <span className="checklist-tab-dot" style={{ background: color }} />
                {c.title}
              </button>
            );
          })}
          <button className="checklist-tab checklist-tab--new" onClick={openCreate}>
            + New
          </button>
        </div>

        {active && (
          <div className="checklist-bar">
            <span className="checklist-title">{active.title}</span>
            <span className="checklist-count">
              {checked.length} / {roster.length} checked
            </span>
            <button className="checklist-action" onClick={openRename}>Rename</button>
            <button className="checklist-action" onClick={handleReset}>Reset</button>
            <button className="checklist-action checklist-action--danger" onClick={handleDelete}>Delete</button>
          </div>
        )}
      </div>

      {!active ? (
        <div className="checklist-empty">
          <p>No checklist yet for {selectedClass?.className}.</p>
          <button className="checklist-tab checklist-tab--new" onClick={openCreate}>
            + Create your first checklist
          </button>
        </div>
      ) : roster.length === 0 ? (
        <p className="checklist-empty">No students in this class yet.</p>
      ) : (
        <div className="check-list">
          {unchecked.map((s) => (
            <StudentCheckRow
              key={s.id}
              name={s.name}
              subtitle={s.present ? null : "Absent"}
              dim={!s.present}
              checked={false}
              onClick={() => handleToggle(s.id)}
            />
          ))}

          {checked.length > 0 && (
            <div className="check-divider"><span>Checked · {checked.length}</span></div>
          )}

          {checked.map((s) => (
            <StudentCheckRow
              key={s.id}
              name={s.name}
              subtitle={s.present ? null : "Absent"}
              dim={false}
              checked={true}
              onClick={() => handleToggle(s.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
